import { useEffect, useState } from 'react';
import { Alert, Badge, Modal, Spinner } from 'react-bootstrap';

import { getEventSources, type EventSource, type EventSourceKind, type EventSourcesResponse } from 'api/events/endpoints/getEventSources';
import { toError } from 'helpers/toError';

import { EntityLink } from './EntityLink';

import './SourcesModal.scss';

interface SourcesModalProps {
  readonly eventId: number;
  readonly onHide: () => void;
}

const GROUP_TITLES: Record<EventSourceKind, string> = {
  node_completion: 'On node completion',
  runtime_limit: 'On a runtime-limit breach',
  status_change_trigger: 'From a status-change trigger',
  file_arrival: 'On file arrival',
};

/** Kinds in the order they are worth reading, not the order they arrive. */
const GROUP_ORDER: readonly EventSourceKind[] = ['node_completion', 'runtime_limit', 'status_change_trigger', 'file_arrival'];

const SourceRow = ({ source }: { readonly source: EventSource }) => {
  switch (source.kind) {
    case 'node_completion':
      return (
        <li>
          <EntityLink id={source.node_definition_id} to={(id) => `/automation/node_definitions?node_definition_id=${id}`}>
            {source.node_name}
          </EntityLink>
          <span className="text-muted ms-2">
            on {source.status ?? 'any end status'}
            {source.precondition !== null && source.precondition !== '' && ` · if ${source.precondition}`}
          </span>
        </li>
      );
    case 'runtime_limit':
      return (
        <li>
          <EntityLink id={source.node_definition_id} to={(id) => `/automation/node_definitions?node_definition_id=${id}`}>
            {source.node_name}
          </EntityLink>
          <span className="text-muted ms-2">
            outside {source.runtime_limit_min ?? '—'}–{source.runtime_limit_max ?? '—'} min
          </span>
        </li>
      );
    case 'status_change_trigger':
      return (
        <li>
          <EntityLink id={source.trigger_id} to={(id) => `/automation/triggers?trigger_id=${id}`}>
            {source.trigger_name}
          </EntityLink>
          <span className="text-muted ms-2">
            {source.watched_node_name ?? 'any node'}
            {source.from_status !== null && ` from ${source.from_status}`}
            {source.to_status !== null && ` to ${source.to_status}`}
          </span>
          {/* These can watch zones other than the event's own, which is otherwise invisible. */}
          {source.monitor_all_zones === true && (
            <Badge bg="info" className="ms-2">
              all zones
            </Badge>
          )}
        </li>
      );
    case 'file_arrival':
      return (
        <li>
          <span>
            {source.directory ?? '—'}/{source.naming_pattern ?? '*'}
          </span>
          <span className="text-muted ms-2">worker {source.worker ?? '—'}</span>
          {/* Configured but switched off is precisely the diagnosis this modal exists for. */}
          {source.enabled === false && (
            <Badge bg="warning" className="ms-2">
              disabled
            </Badge>
          )}
        </li>
      );
  }
};

/**
 * Everything that can raise one event, grouped by kind.
 *
 * Manual raise is deliberately absent: every event can be raised by hand, so
 * listing it would tell the operator nothing and would bury the diagnosis this
 * panel exists for — an empty list means nothing feeds this event
 * automatically, which is why it is starving. The card's Raise button is the
 * manual affordance.
 */
export const SourcesModal = ({ eventId, onHide }: SourcesModalProps) => {
  const [data, setData] = useState<EventSourcesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    getEventSources(eventId)
      .then((response) => {
        if (current) setData(response);
      })
      .catch((loadError) => {
        if (current) setError(toError(loadError).message);
      });
    return () => {
      current = false;
    };
  }, [eventId]);

  return (
    <Modal show onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>What can raise this{data === null ? '' : ` — ${data.event.name}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error !== null && <Alert variant="danger">{error}</Alert>}
        {error === null && data === null && <Spinner animation="border" size="sm" />}

        {data !== null && data.sources.length === 0 && (
          <p className="text-muted mb-0">Nothing raises this event automatically. It can only be raised by hand, from a card&apos;s Raise button.</p>
        )}

        {data !== null &&
          GROUP_ORDER.map((kind) => {
            const group = data.sources.filter((source) => source.kind === kind);
            if (group.length === 0) {
              return null;
            }
            return (
              // A div, not a <section>: the theme gives a bare section 3.5rem
              // of vertical padding, which is page-level spacing inside a modal.
              <div key={kind} className="sources-modal__group">
                <div className="sources-modal__heading">
                  <span className="sources-modal__dot" aria-hidden="true" />
                  {GROUP_TITLES[kind]}
                </div>
                <ul className="list-unstyled mb-0">
                  {group.map((source, index) => (
                    <SourceRow key={`${kind}-${index}`} source={source} />
                  ))}
                </ul>
              </div>
            );
          })}
      </Modal.Body>
    </Modal>
  );
};
